import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Popover, PopoverContent, PopoverTrigger, Spinner, Text } from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { Check, ChevronDown, Plus, Search } from "lucide-react";

export interface TagOption {
    id: string;
    name: string;
}

interface TagPickerProps {
    id?: string;
    tags: TagOption[];
    selectedIds: readonly string[];
    onChange: (tagIds: string[]) => void;
    disabled?: boolean;
    /**
     * Creates a tag and resolves with it, or undefined when it could not. Omitted for anyone who may
     * not create tags — `createTag` is ADMIN/SYSTEM only — and the option is not offered at all.
     */
    onCreate?: (name: string) => Promise<TagOption | undefined>;
}

const normalize = (name: string) => name.trim().toLowerCase();

/**
 * Every tag in a dropdown, any number checked, and — for those allowed — a new one made from the
 * search box when nothing matches it.
 *
 * Exact matches are compared case-insensitively before offering to create, because tag names are
 * unique and "cravings" beside "Cravings" is a duplicate the database would refuse anyway.
 */
export function TagPicker({ id, tags, selectedIds, onChange, disabled = false, onCreate }: TagPickerProps) {
    const { t } = useTranslation();
    const [
        open,
        setOpen,
    ] = useState<boolean>(false);
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        creating,
        setCreating,
    ] = useState<boolean>(false);

    const selected = new Set(selectedIds);
    const selectedTags = tags.filter((tag) => selected.has(tag.id));
    const term = normalize(search);
    const visible = useMemo(
        () => (term ? tags.filter((tag) => normalize(tag.name).includes(term)) : tags),
        [
            tags,
            term,
        ],
    );
    const exact = tags.find((tag) => normalize(tag.name) === term);
    const canCreate = !!onCreate && term.length > 0 && !exact;

    const toggle = (tagId: string) =>
        onChange(
            selected.has(tagId)
                ? selectedIds.filter((x) => x !== tagId)
                : [
                      ...selectedIds,
                      tagId,
                  ],
        );

    const create = async () => {
        if (!onCreate || !canCreate || creating) return;
        setCreating(true);
        try {
            const tag = await onCreate(search.trim());
            if (tag) {
                onChange([
                    ...selectedIds,
                    tag.id,
                ]);
                setSearch("");
            }
        } finally {
            setCreating(false);
        }
    };

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setSearch("");
            }}
        >
            <PopoverTrigger asChild>
                <button
                    id={id}
                    type="button"
                    disabled={disabled}
                    className={cn(
                        "border-input bg-background flex min-h-10 w-full items-center gap-2 rounded-lg border px-3 py-1.5 text-left text-sm transition-colors",
                        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60",
                        open && "ring-ring ring-2",
                    )}
                >
                    <span className="flex min-w-0 flex-1 flex-wrap gap-1">
                        {selectedTags.length === 0 ? (
                            <span className="text-muted-foreground">{t("admin.bulkUpload.tagsPlaceholder")}</span>
                        ) : (
                            selectedTags.map((tag) => (
                                <span
                                    key={tag.id}
                                    className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs"
                                >
                                    {tag.name}
                                </span>
                            ))
                        )}
                    </span>
                    <ChevronDown
                        aria-hidden
                        className={cn(
                            "text-muted-foreground h-4 w-4 shrink-0 transition-transform",
                            open && "rotate-180",
                        )}
                    />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-0">
                <div className="border-border relative border-b p-2">
                    <Search className="text-muted-foreground absolute top-1/2 left-4 h-3.5 w-3.5 -translate-y-1/2" />
                    <Input
                        autoFocus
                        aria-label={
                            onCreate ? t("admin.bulkUpload.tagSearchOrCreate") : t("admin.bulkUpload.tagFilter")
                        }
                        placeholder={
                            onCreate ? t("admin.bulkUpload.tagSearchOrCreate") : t("admin.bulkUpload.tagFilter")
                        }
                        className="h-8 pl-8 text-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            // Enter picks the tag typed out in full, or makes it when there is none.
                            if (exact) toggle(exact.id);
                            else void create();
                        }}
                    />
                </div>
                <div role="group" aria-label={t("admin.bulkUpload.fieldTags")} className="max-h-60 overflow-y-auto p-1">
                    {visible.map((tag) => {
                        const on = selected.has(tag.id);
                        return (
                            <button
                                key={tag.id}
                                type="button"
                                aria-pressed={on}
                                onClick={() => toggle(tag.id)}
                                className="hover:bg-muted focus-visible:bg-muted flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm focus-visible:outline-none"
                            >
                                <span
                                    aria-hidden
                                    className={cn(
                                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                        on ? "border-primary bg-primary text-primary-foreground" : "border-border",
                                    )}
                                >
                                    {on && <Check className="h-3 w-3" strokeWidth={3} />}
                                </span>
                                <span className="truncate">{tag.name}</span>
                            </button>
                        );
                    })}
                    {visible.length === 0 && !canCreate && (
                        <Text size="xs" muted className="px-2 py-3 text-center">
                            {tags.length === 0 ? t("admin.bulkUpload.noTags") : t("admin.bulkUpload.noTagsMatch")}
                        </Text>
                    )}
                </div>
                {canCreate && (
                    <div className="border-border border-t p-1">
                        <button
                            type="button"
                            disabled={creating}
                            onClick={() => void create()}
                            className="text-primary hover:bg-muted focus-visible:bg-muted flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium focus-visible:outline-none disabled:opacity-60"
                        >
                            {creating ? <Spinner className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                            <span className="truncate">{t("admin.bulkUpload.createTag", { name: search.trim() })}</span>
                        </button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
